/**
 * Oppdateringsintervall og karusell for WMS-dashboardet.
 *
 * Valgene lagres i dashboardoppsettet (localStorage), ikke i databasen:
 * - dashboard: layout.refreshInterval (mangler = hver widgets eget standardintervall)
 * - widget:    settings.refreshOverride (mangler = følg dashboardet)
 */
import type { DashboardLayout, WidgetInstance } from '../types';

/** Valg i nedtrekkslistene (sekunder, 0 = av). */
export const REFRESH_CHOICES = [15, 30, 60, 120, 300, 600, 0] as const;

/** Standard visningstid per dashboard i karusellen. */
export const DEFAULT_CAROUSEL_SECONDS = 45;
export const MIN_CAROUSEL_SECONDS = 5;
export const MAX_CAROUSEL_SECONDS = 3600;

/** Intervallet en widget faktisk bruker: widgetens overstyring → dashboardets valg → widgetens standard. */
export function effectiveRefreshSeconds(instance: WidgetInstance, layout?: DashboardLayout): number {
  if (typeof instance.settings.refreshOverride === 'number') return instance.settings.refreshOverride;
  if (typeof layout?.refreshInterval === 'number') return layout.refreshInterval;
  return instance.settings.refreshInterval;
}

type Translate = (key: string, params?: Record<string, string | number | null | undefined>, fallback?: string) => string;

/** «30 s», «2 min», «Av». */
export function formatInterval(seconds: number, t: Translate): string {
  if (seconds <= 0) return t('manager.modules.wmsDashboard.refresh.off', {}, 'Av');
  if (seconds < 60) return t('manager.modules.wmsDashboard.refresh.seconds', { '0': seconds }, `${seconds} s`);
  const minutes = Math.round(seconds / 60);
  return t('manager.modules.wmsDashboard.refresh.minutes', { '0': minutes }, `${minutes} min`);
}

/** Dashboardene i karusellen, i rekkefølge, med visningstid. */
export function carouselLayouts(layouts: DashboardLayout[]): { layout: DashboardLayout; seconds: number }[] {
  return layouts
    .map((layout, index) => ({ layout, index }))
    .filter(({ layout }) => layout.carousel?.include !== false)
    .sort((a, b) => (a.layout.carousel?.order ?? a.index) - (b.layout.carousel?.order ?? b.index))
    .map(({ layout }) => ({ layout, seconds: carouselSeconds(layout) }));
}

export function carouselSeconds(layout: DashboardLayout): number {
  const seconds = layout.carousel?.seconds ?? DEFAULT_CAROUSEL_SECONDS;
  return Math.min(MAX_CAROUSEL_SECONDS, Math.max(MIN_CAROUSEL_SECONDS, Math.round(seconds)));
}
